import React, { useLayoutEffect, useRef } from "react";

/**
 * #135 — Time Machine Diff Pair row-height sync.
 *
 * Wraps a single row in either grid (LEFT/T1 or RIGHT/T2). Measures its own
 * offsetHeight via ResizeObserver and reports up to a shared coordinator;
 * reads back the synced `min-height` (the max of both sides' reported heights)
 * and applies it as inline style so the two grids' matching rows always
 * render at the same height — preserving row-by-row alignment under
 * synchronized scroll regardless of content differences between T1 and T2.
 *
 * Pure helpers (`recordRowHeight` / `computeSyncedRowMinHeight`) live in
 * `src/utility/diffPairRowHeights.js` and have full adversarial test coverage.
 * This wrapper is the React glue — measurement effect + style application.
 *
 * When `reportRowHeight` / `getRowMinHeight` are not provided (e.g. live mode,
 * single-grid Time Travel, or the legacy Scheduler tab), this component
 * degrades to a plain `<div>` with the original className — no observers,
 * no style overhead. Callers can use this wrapper unconditionally.
 *
 * Stability note: applying min-height to a row whose natural content is
 * shorter bumps offsetHeight up to that min-height. The next ResizeObserver
 * fire reports the inflated height, which the coordinator records — but
 * since `max(left, right)` is unchanged, the coordinator returns the same
 * min-height and React skips the re-render via reference-equality. The
 * cycle is stable and converges within one observer-fire round.
 *
 * Shrink-to-fit caveat: once a side's row has been padded up by min-height,
 * the natural content size is no longer observable from offsetHeight alone.
 * To shed an obsolete tall min-height, something must force a remount of
 * the row (week navigation, exit/re-enter Time Travel, toggle Diff Pair).
 * For the bug this addresses (visible misalignment under shared scroll),
 * shrinkage lag is acceptable; the row will look slightly oversized for one
 * Diff Pair session at most.
 *
 * Props:
 *   rowId            — stable identifier shared between both grids ("unassigned"
 *                      for the Unassigned row, payrollNo for crew rows)
 *   side             — "left" | "right", which grid we are
 *   reportRowHeight  — (rowId, side, height) => void from the coordinator
 *   getRowMinHeight  — (rowId) => number|undefined from the coordinator
 *   children         — the actual row content (cells, etc.)
 *   className        — Tailwind classes for the row's flex layout — preserved
 *                      in the degraded "no sync" mode too
 *   syncKey          — optional debug-only key for the rowId; React itself
 *                      handles remounting via the parent's array .map() key
 */
const DiffPairRowSync = ({
  rowId,
  side,
  reportRowHeight,
  getRowMinHeight,
  children,
  className,
}) => {
  const ref = useRef(null);

  // ResizeObserver lifecycle. Only attach when the coordinator props are
  // supplied — keeps the degraded path free of side effects.
  useLayoutEffect(() => {
    if (!reportRowHeight || !side || !rowId) return;
    const el = ref.current;
    if (!el || typeof window === "undefined" || typeof window.ResizeObserver !== "function") {
      // Best-effort fallback for older browsers / SSR / jsdom: take one
      // synchronous measurement now. Without ResizeObserver, content
      // changes won't re-trigger, but the initial render gets aligned.
      if (el) reportRowHeight(rowId, side, el.offsetHeight);
      return;
    }
    const ro = new window.ResizeObserver(() => {
      if (ref.current) {
        reportRowHeight(rowId, side, ref.current.offsetHeight);
      }
    });
    ro.observe(el);
    // Also fire a synchronous initial measurement so the first paint after
    // mount is already at the synced height (ResizeObserver's first fire is
    // dispatched async and may arrive after paint).
    reportRowHeight(rowId, side, el.offsetHeight);
    return () => ro.disconnect();
  }, [rowId, side, reportRowHeight]);

  // Even with no reportRowHeight, we still might want to read the min-height
  // if the coordinator has it from the OTHER side's measurements. But
  // typically both grids participate symmetrically; degraded mode skips this.
  const minHeight =
    typeof getRowMinHeight === "function" ? getRowMinHeight(rowId) : undefined;

  // Degraded mode — no rowId or no coordinator. Plain div with className only.
  if (!rowId || !reportRowHeight) {
    return <div className={className}>{children}</div>;
  }

  const style = minHeight ? { minHeight: `${minHeight}px` } : undefined;
  return (
    <div ref={ref} className={className} style={style}>
      {children}
    </div>
  );
};

export default DiffPairRowSync;
